/**
 * Контракт настроек пользователя — **единственная точка правды** для
 * полей, которыми клиент обменивается с бэкендом по разделам «Профиль»
 * и «Аккаунт» модалки настроек. Если backend-разработчик переименует
 * поле или поменяет формат — правим **здесь** (типы + мапперы), и
 * остальной код (слайсы, селекторы, форма) автоматически поедет с
 * новым контрактом.
 *
 * Соответствующие эндпоинты — в `src/api/apiConfig.ts`:
 *   profileSettings → PATCH  /api/profile/settings
 *   accountSettings → PATCH  /api/account/settings
 *   accountDelete   → DELETE /api/account
 */

import type { ProfileFormValues, AccountFormValues } from '@/components/features/settings/types';

// -----------------------------------------------------------------------------
// UI-форма профиля
// -----------------------------------------------------------------------------

/**
 * Состояние слайса профиля.
 *
 * `data` хранит форму в «UI-виде» — в том же формате, что рендерит
 * `ProfileSettingsForm` (`dobDay/dobMonth/dobYear` как отдельные строки,
 * `gender` допускает пустую строку до выбора). Перед отправкой на сервер
 * `data` пропускается через `toProfileSettingsPayload`, который собирает
 * `dob` в ISO-формат и сужает `gender`.
 */
export interface ProfileSettingsState {
	data: ProfileFormValues;
	successMessage: string | null;
	error: string | null;
	isFetching: boolean;
}

/**
 * === Контракт запроса: PATCH /api/profile/settings ===
 *
 * Request body (JSON):
 * ```json
 * {
 *   "gender": "male" | "female",     // обязателен
 *   "name":   "string",              // обязателен, trimmed, min 2
 *   "dob":    "YYYY-MM-DD" | null    // null = не задана
 * }
 * ```
 *
 * Success:  200 OK, body игнорируется.
 * Errors:
 *   - 400 — `{ detail: "<перевод ключа validation.*>" }` (будет выведено
 *     как `error` в слайсе через `handleResponseError`).
 *   - 401 — требует авторизацию.
 */
export interface ProfileSettingsPayload {
	gender: 'male' | 'female';
	name: string;
	dob: string | null;
}

/**
 * UI-форма → payload для API. Единственное место, где склеивается дата
 * рождения и сужается `gender`. Backend меняет контракт — правим тут.
 */
export const toProfileSettingsPayload = (values: ProfileFormValues): ProfileSettingsPayload => {
	const { dobDay, dobMonth, dobYear } = values;
	const dob = dobDay && dobMonth && dobYear
		? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
		: null;

	return {
		gender: values.gender as 'male' | 'female',
		name: values.name.trim(),
		dob,
	};
};

// -----------------------------------------------------------------------------
// UI-форма аккаунта
// -----------------------------------------------------------------------------

export interface AccountSettingsState {
	data: AccountFormValues;
	successMessage: string | null;
	error: string | null;
	isFetching: boolean;
}

/**
 * === Контракт запроса: PATCH /api/account/settings ===
 *
 * Request body (JSON):
 * ```json
 * {
 *   "email":    "user@example.com",  // обязателен, валидный e-mail
 *   "password": "string" | null      // null = пароль не менять
 * }
 * ```
 *
 * Success: 200 OK, body игнорируется.
 * Errors:
 *   - 400 — `{ detail: "..." }`
 *   - 409 — e-mail уже занят, ожидается `{ detail: "..." }`
 *
 * === Контракт запроса: DELETE /api/account ===
 * Body отсутствует. Success: 204 No Content.
 */
export interface AccountSettingsPayload {
	email: string;
	password: string | null;
}

export const toAccountSettingsPayload = (values: AccountFormValues): AccountSettingsPayload => ({
	email: values.email.trim(),
	password: values.password ? values.password : null,
});
